import { openModal } from "action/modal/openModal";
import { IProductReview } from "api/interfaces/product-review.type";
import StarRatings from "component/design-system/StarRatings";
import PAButton, { BUTTON_SIZE, BUTTON_TYPE } from "component/design-system/buttons/PAButton";
import SliderV2 from "component/design-system/carousel/SliderV2";
import PAImage from "component/design-system/images/PAImage";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import ReadMore from "component/read-more/ReadMore";
import useScreenSize from "hoc/useScreenSize";
import { useTranslation } from "next-i18next";
import { useDispatch } from "react-redux";
import HideIf from "util/HideIf";
import { formatDate } from "util/date-helper";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import styles from "./singleReview.module.scss";

interface IProps {
  review: IProductReview;
  className?: string;
  handleEditReview: (review: IProductReview) => void;
  postDeleteReview: () => void;
}

function SingleReviewCard({
  review, className, handleEditReview, postDeleteReview,
}: IProps) {
  const {
    reviewId, files, reviewTitle, verifiedBuyer, reviewerName, location, rating, review: reviewText, productRecommended, createdDateTime,
  } = review;

  const dispatch = useDispatch();
  const { t } = useTranslation("product");

  const [isMobile, , isDesktop] = useScreenSize();

  const openImagesModal = (idx: number) => {
    dispatch(openModal("thumbnailImage", {
      images: files,
      imageIndex: idx,
      title: reviewTitle,
    }));
  };

  return (
    <div className={`${styles.single_review_container} ${isMobile ? styles.single_review_container_mobile : ""} ${className}`}>
      <div className={styles.review_details}>
        <StarRatings
          rating={rating}
          filled
        />
        <PAText fontSize={FONT_SIZE[2]} bold className="m-t-10 m-r-10 review_title">{reviewTitle}</PAText>

        <PAText className="m-b-5" color="medium-grey">{reviewerName}</PAText>
        <div className={styles.reviewer_info}>
          <PAText color="grey" fontSize={FONT_SIZE[0]} className="m-r-10">
            {t("reviewed_in", { location, date: formatDate(createdDateTime) })}
          </PAText>
          {verifiedBuyer && (
            <div className={styles.verified_purchase}>
              <i className="fa fa-certificate" />
              <PAText fontSize={FONT_SIZE[0]}>{t("Verified Purchase")}</PAText>
            </div>
          )}
        </div>

        <div className={styles.review_text}>
          {isMobile
            ? <ReadMore text={reviewText} lineClamp={5} />
            : <PAText fontSize={FONT_SIZE[2]}>{reviewText}</PAText>}
        </div>

        {files.length > 0 && (
          <SliderV2
            ulClassName={styles.review_images_list}
            className={`${styles.review_images_container} ${files.length > 3 && isDesktop ? styles.review_images_container_arrows : ""}`}
            slidesInView={3}
            showArrows={files.length > 3 && isDesktop}
            autoplay={false}
            showDots={false}
          >
            {files.map((file: string, idx) => (
              <div className={styles.review_images_container} key={file} onClick={() => openImagesModal(idx)}>
                <PAImage
                  height={88}
                  src={getRelativeImagePath(file)}
                  alt={reviewTitle}
                  className={styles.review_image}
                />
              </div>
            ))}
          </SliderV2>
        )}

        {productRecommended ? (
          <div className={styles.review_recommend}>
            <i className="fa fa-check-circle" aria-hidden="true" />

            <PAText bold className="m-r-5">{t("Yes, ")}</PAText>
            <PAText color="grey">{t("I recommend this product.")}</PAText>
          </div>
        ) : (
          <div className={styles.review_recommend}>
            <i className="fa fa-times-circle" aria-hidden="true" />

            <PAText bold className="m-r-5">{t("No, ")}</PAText>
            <PAText color="grey">{t("I do not recommend this product.")}</PAText>
          </div>
        )}
        <HideIf show={review.editable}>
          <div className="d-flex">
            <PAButton
              buttonType={BUTTON_TYPE.OUTLINE}
              widthAuto
              className="m-r-5 p-t-0 p-b-0 p-lr-10"
              onClick={handleEditReview}
              size={BUTTON_SIZE.SMALL}
            >
              {t("Edit")}

            </PAButton>
            <PAButton
              buttonType={BUTTON_TYPE.OUTLINE}
              widthAuto
              onClick={() => dispatch(openModal("deleteReviewModal", { reviewId, postDeleteReview }))}
              size={BUTTON_SIZE.SMALL}
              className="p-t-0 p-b-0 p-lr-10"
            >
              {t("Delete")}

            </PAButton>
          </div>
        </HideIf>
      </div>
    </div>
  );
}

export default SingleReviewCard;
